import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star } from "lucide-react";

const HERO_IMAGE =
  "https://customer-assets-0z36b82j.emergentagent.net/job_everyday-living-3/artifacts/asj2en55_f1d3f66b99eb867c473dc83a7e935b5e.jpg";

const LINES = ["The everyday things,", "chosen properly."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[86vh] min-h-[540px] overflow-hidden bg-neutral-900">
      <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
        <img src={HERO_IMAGE} alt="Dark luxury living room interior styled with Everyday Essentials products" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/25 to-transparent" />

      <div className="relative z-10 mx-auto flex h-full max-w-[1440px] items-center px-6 sm:px-8 lg:px-12">
        <div className="max-w-xl">
          <h1 data-testid="hero-headline" className="font-script text-5xl font-semibold leading-[1.08] text-white sm:text-6xl lg:text-7xl">
            {LINES.map((line, i) => (
              <span key={line} className="-mb-2 block overflow-hidden pb-3">
                <motion.span
                  className="block"
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-4 max-w-md text-sm leading-relaxed text-white/85 sm:text-base"
          >
            Carefully selected, high-quality, and affordable products designed to streamline your daily routine.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-8 flex flex-col items-start gap-4"
          >
            <Link
              to="/shop"
              data-testid="hero-shop-now"
              className="rounded-full border border-white/80 px-10 py-3 text-[13px] font-semibold uppercase tracking-[0.18em] text-white transition-all duration-300 hover:bg-white hover:text-neutral-900"
            >
              Shop Now
            </Link>
            <Link
              to="/best-sellers"
              data-testid="hero-explore-best-sellers"
              className="rounded-full bg-white px-10 py-3.5 text-sm font-semibold text-neutral-900 transition-colors duration-300 hover:bg-neutral-200"
            >
              Explore the best sellers
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-10 flex items-center gap-2 text-xs text-white/70"
          >
            <Star size={13} className="fill-amber-400 text-amber-400" />
            <span className="font-medium text-white/85">4.8/5</span> from 15,000+ everyday shoppers across India.
          </motion.div>
        </div>
      </div>
    </section>
  );
}
